import {
  PeopleApi,
  PersonResponse,
  CreatePersonRequest,
  AddFamilyMemberRequest,
  UpdatePersonRequest,
  type RoleExplanationResponse
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup, safeFetchCall } from './apiWrapper';

/**
 * Wrapped people API with standardized error handling
 */
class WrappedPeopleApi {
  /**
   * Create a new person
   */
  async createPerson(personData: CreatePersonRequest): Promise<ApiResult<PersonResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.createPerson(personData)
    );
  }

  /**
   * Add a new family member as a child of an existing member.
   * Creates the person, family member mapping, and parent-child relationship.
   */
  async addFamilyMember(request: AddFamilyMemberRequest): Promise<ApiResult<PersonResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.addFamilyMember(request)
    );
  }

  /**
   * Get a person by family member ID
   * Note: The API endpoint accepts familyMemberId, not personId
   */
  async getPerson(familyMemberId: string): Promise<ApiResult<PersonResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.getPerson(familyMemberId)
    );
  }

  /**
   * Update a person by family member ID
   * Note: The API endpoint accepts familyMemberId, not personId
   */
  async updatePerson(familyMemberId: string, personData: UpdatePersonRequest): Promise<ApiResult<PersonResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.updatePerson(familyMemberId, personData)
    );
  }

  /**
   * Delete a person by family member ID
   * Note: The API endpoint accepts familyMemberId, not personId
   */
  async deletePerson(familyMemberId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.deletePerson(familyMemberId)
    );
  }

  /**
   * Claim a profile
   */
  async claimProfile(familyMemberId: string): Promise<ApiResult<PersonResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.claimProfile(familyMemberId)
    );
  }

  /**
   * Unclaim a profile by family member ID
   * Note: The API endpoint accepts familyMemberId, not personId
   */
  async unclaimProfile(familyMemberId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.unclaimProfile(familyMemberId)
    );
  }

  /**
   * Check if user has claimed a profile in a family
   * This method uses direct fetch as it's not part of the generated SDK
   */
  async checkUserClaimedProfile(familyId: string): Promise<ApiResult<string | null>> {
    const result = await safeFetchCall<{ familyMemberId?: string }>(async () => {
      const configuration = await getApiConfiguration();
      if (!configuration) throw new Error('Failed to get API configuration');

      return fetch(`${configuration.basePath}/v2/people/check-claimed-profile/${familyId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${configuration.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
    }, 'checkUserClaimedProfile');

    if (result.success) {
      return { success: true, data: result.data.familyMemberId || null };
    }
    return result;
  }

  /**
   * Check if the user can modify a family member
   */
  async canModifyMember(familyMemberId: string): Promise<ApiResult<boolean>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      async (api) => {
        const response = await api.canModifyMember(familyMemberId);
        return { data: response.data.canModify };
      }
    );
  }

  /**
   * Batch check if the user can modify multiple family members.
   * Use this when displaying a list of members to reduce API calls.
   * IDs are sent in chunks of 100 to respect the API limit; results are merged.
   */
  async canModifyMembers(familyMemberIds: string[]): Promise<ApiResult<Record<string, boolean>>> {
    const BATCH_SIZE = 100;
    if (familyMemberIds.length === 0) {
      return { success: true, data: {} };
    }
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      async (api) => {
        const canModify: Record<string, boolean> = {};
        for (let i = 0; i < familyMemberIds.length; i += BATCH_SIZE) {
          const chunk = familyMemberIds.slice(i, i + BATCH_SIZE);
          const response = await api.canModifyMembersBatch({ familyMemberIds: chunk });
          Object.assign(canModify, response.data.canModify);
        }
        return { data: canModify };
      }
    );
  }

  /**
   * Get role explanation for a user's access to a family member
   */
  async getRoleExplanation(familyMemberId: string): Promise<ApiResult<RoleExplanationResponse>> {
    return withApiSetup(
      (config) => new PeopleApi(config),
      getApiConfiguration,
      (api) => api.getRoleExplanation(familyMemberId)
    );
  }
}

/**
 * Wrapped people API instance with standardized error handling
 */
export const peopleApi = new WrappedPeopleApi();

export default peopleApi;

// Re-export SDK types for convenience
export type { RoleExplanationResponse } from 'we-tree-api-client/v2';
